// @ts-nocheck
import React, { useCallback } from 'react';
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Stack,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import DialogTransition from '../Common/Transition/DialogTransition';
import DocumentEditor from './DocumentEditor/DocumentEditor';
import swalToast from '../Common/swalToast';
import { downloadBlobResponse } from '../../utils/Utils';
import useDocumentInventory from '../../hooks/ModelInventory/useDocumentInventory';
import PdfIframe from './PdfIframe';

const DocumentPreviewModal = ({
  open,
  handleClose,
  saveAsNewVersion,
  updateDocument,
  getVersionDetails,
  getDocumentMarkdown,
  selectedDocumentForEdit,
  setSelectedDocumentForEdit,
}) => {
  const { downloadDocument } = useDocumentInventory();
  const {
    file_type,
    is_editable,
    document_name,
    document_id,
    entity_id,
    entity_type,
  } = selectedDocumentForEdit || {};
  const isPDFFile = file_type === 'pdf';
  // Fallback to handle old files as they don't have is_editable attr.
  const isUnsupportedFileFallBack = !['docx', 'pdf', 'txt', 'md'].includes(
    file_type
  );
  const isUnsupportedFile =
    is_editable === undefined ? isUnsupportedFileFallBack : !is_editable;

  /**
   * Closes dialogue, resets the associated Formik form, and triggers the closure of the main modal.
   * @returns {void} - No return value.
   */
  const handleDialogueClose = useCallback(() => {
    handleClose();
    setSelectedDocumentForEdit({});
  }, [handleClose, setSelectedDocumentForEdit]);

  const fetchDocumentsList = useCallback(() => {}, []);

  /**
   * Downloads a document based on the provided row information.
   *
   * @param {Object} row - The row object containing document information.
   * @returns {void}
   */
  const downloadTemplate = (downloadData, documentName, fileType) => {
    try {
      downloadBlobResponse(
        { data: downloadData },
        `${documentName}.${fileType}`
      );
    } catch (e) {
      swalToast({
        icon: 'error',
        title: `Error occurred at time of downloading file. ${e}`,
      });
    }
  };
  const handleDownload = useCallback(() => {
    downloadDocument({
      document_id,
      entity_id,
      entity_type,
      export_type: file_type,
      commit_id:
        document?.versions?.length > 0
          ? `${document?.versions[document.versions.length - 1]?.version}`
          : null,
    }).then((response) => {
      if (response) {
        if (response?.data?.export_type === 'onedrive_docx') {
          window.open(response?.data?.path, '_blank');
        } else {
          downloadTemplate(response?.data, document_name, file_type);
        }
      }
    });
  }, [downloadDocument, selectedDocumentForEdit, downloadTemplate]);

  const renderUnsupportedFileWarning = () => {
    if (isUnsupportedFile) {
      return (
        <Stack
          sx={{
            padding: '32px 16px',
            alignItems: 'center',
            gap: '32px',
          }}
        >
          <Typography textAlign="justify">
            This file cannot be previewed here. Please download it to view or
            edit.
          </Typography>
          <Button variant="contained" onClick={handleDownload}>
            Download file
          </Button>
        </Stack>
      );
    }
    return null;
  };

  return (
    <Box>
      <Dialog
        maxWidth="xl"
        fullWidth={true}
        open={open}
        onClose={handleDialogueClose}
        TransitionComponent={DialogTransition}
      >
        <DialogTitle>
          <Box component="span" aria-live="polite" aria-atomic="true">
            {document_name}
          </Box>
        </DialogTitle>

        <IconButton
          color="primary"
          aria-label="Close dialog"
          onClick={handleDialogueClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers>
          {renderUnsupportedFileWarning()}
          {!isUnsupportedFile && !isPDFFile && (
            <Grid container rowSpacing={5} xs={12}>
              <Grid item xs={12}>
                <DocumentEditor
                  mode="view"
                  docDetails={selectedDocumentForEdit}
                  getDocumentMarkdown={getDocumentMarkdown}
                  getVersionDetails={getVersionDetails}
                  updateDocument={updateDocument}
                  // isUserAuthorized={isUserAuthorized}
                  saveAsNewVersion={saveAsNewVersion}
                  fetchDocumentsList={fetchDocumentsList}
                />
              </Grid>
            </Grid>
          )}
          {!isUnsupportedFile && isPDFFile && (
            <Grid container xs={12}>
              <Grid item xs={12}>
                <PdfIframe
                  docDetails={selectedDocumentForEdit}
                  downloadDocument={getDocumentMarkdown}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {!isUnsupportedFile && isPDFFile ? (
            <Box>
              <Button variant="contained" onClick={handleDialogueClose}>
                Close
              </Button>
            </Box>
          ) : null}
          {!isUnsupportedFile && !isPDFFile ? (
            <Box>
              <Button
                variant="contained"
                title="close"
                onClick={handleDialogueClose}
              >
                Close
              </Button>
            </Box>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
DocumentPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  saveAsNewVersion: PropTypes.func.isRequired,
  updateDocument: PropTypes.func.isRequired,
  getVersionDetails: PropTypes.func.isRequired,
  getDocumentMarkdown: PropTypes.func.isRequired,
  setSelectedDocumentForEdit: PropTypes.func.isRequired,
  selectedDocumentForEdit: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default DocumentPreviewModal;
